/*
 * Default style presets + system prompt templates for local mode. Mirrors the
 * backend's V2DefaultsSeeder so localStorage looks identical to a freshly-
 * seeded production DB.
 *
 * The two system templates use the same fixed UUIDs as the backend
 * (PromptTemplate::SYSTEM_IMAGE_ID / SYSTEM_VIDEO_ID) so episodes that
 * round-trip between local and prod don't get duplicate-template confusion.
 */

import {
  type PromptTemplate,
  type StylePreset,
  SYSTEM_IMAGE_TEMPLATE_ID,
  SYSTEM_VIDEO_TEMPLATE_ID,
} from '@/types';

const IMAGE_MODEL = 'gemini-3-pro-image-preview';
const VIDEO_MODEL = 'veo-3.1-fast-generate-preview';

export function defaultStylePresets(): StylePreset[] {
  return [
    {
      id: 'preset-cinematic-3d',
      name: 'Cinematic 3D',
      description: 'High-end 3D animation aesthetic for kids (the studio default).',
      isDefault: true,
      styleReference: {
        image: '',
        description: [
          'Style requirements:',
          '- High-end 3D animation style for kids (Pixar/Disney Junior/Cocomelon/Illumination aesthetic).',
          '- Cute, friendly, and round character designs with appealing proportions.',
          '- Soft, warm, cinematic lighting.',
          '- Vibrant, cheerful color palette.',
          '- Detailed textures but with a soft, inviting look.',
          '- HD 4K resolution.',
          '- 16:9 aspect ratio.',
        ].join('\n'),
      },
      defaultAspectRatio: '16:9',
      imageModel: IMAGE_MODEL,
      videoModel: VIDEO_MODEL,
    },
    {
      id: 'preset-cinematic-2d',
      name: 'Cinematic 2D',
      description: "Soft, painterly children's book illustration style.",
      isDefault: false,
      styleReference: {
        image: '',
        description: [
          'Style requirements:',
          "- Soft, painterly children's book illustration style.",
          '- Whimsical, cozy, and warm atmosphere.',
          '- Cute, expressive characters with soft edges and rounded shapes.',
          '- Rich, vibrant natural colors with golden-hour or soft diffused lighting.',
          '- Detailed but painterly backgrounds (like a high-quality modern storybook).',
          '- NOT flat vector art or harsh outlines.',
        ].join('\n'),
      },
      defaultAspectRatio: '16:9',
      imageModel: IMAGE_MODEL,
      videoModel: VIDEO_MODEL,
    },
  ];
}

/**
 * The two immutable system templates. Bodies match the backend seeder
 * exactly. Re-seed-friendly: edit here AND in V2DefaultsSeeder when
 * iterating on the legacy ROLE/STYLE/TASK scaffold.
 */
export function defaultSystemTemplates(): PromptTemplate[] {
  return [
    {
      id: SYSTEM_IMAGE_TEMPLATE_ID,
      scope: 'global',
      episodeUuid: null,
      kind: 'image',
      isSystem: true,
      clonedFromId: null,
      name: 'Default image scaffold',
      body: [
        '**ROLE**: Professional Illustrator for High-Budget Animation.',
        '**STYLE**: {style}',
        '**TASK**: Create a scene matching the following description.',
        '**PROMPT**: "{prompt}"',
        '',
        '**REQUIREMENTS**:',
        '- **CHARACTERS**: {cast}',
        '- **CONTEXT**: {previous_scenes}',
        '- **SCENE**: Change the scene direction and apply camera angles.',
        '- **RESTRICTIONS**: {cultural_guidelines}',
        "- **SAFETY**: G-rated children's content only.",
        '',
        '**CRITICAL SCALE & PERSPECTIVE RULES**:',
        '- WIDE/AERIAL/CITY VIEWS: Characters MUST be REALISTIC SMALL SCALE relative to buildings.',
        '- CAMERA CONSISTENCY: The camera angle MUST be maintained.',
        '- PROPER DEPTH: Use atmospheric perspective.',
        '',
        'Aspect ratio: {aspect_ratio}.',
      ].join('\n'),
    },
    {
      id: SYSTEM_VIDEO_TEMPLATE_ID,
      scope: 'global',
      episodeUuid: null,
      kind: 'video',
      isSystem: true,
      clonedFromId: null,
      name: 'Default video scaffold',
      body: '{prompt} Animation style, smooth motion. Audio: Include clear speech and dialogue if described in the prompt. NO background music, NO foreground music, NO instruments. Only natural ambient sounds and sound effects.',
    },
  ];
}

/**
 * Cultural-guidelines text used to substitute the {cultural_guidelines}
 * placeholder when the toggle is on. Mirrors config('studio.cultural_guidelines')
 * in the Laravel backend.
 */
export const CULTURAL_GUIDELINES_TEXT = [
  '- Women MUST wear hijab and loose, modest full-length dresses. NO PANTS. NO skin visible except face and hands.',
  '- Architecture of villages or cities MUST be Islamic.',
  '- Absolutely NO churches or crosses.',
].join('\n');
